import api from './api';
import { BACKEND_CONFIG, buildUrl, buildQueryString } from '../config/backend';
import { StatisticsResponse, RoleStatistics } from '../types/statistics';

export interface Firm {
  id: number;
  name: string;
  slug: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  description: string;
  logo_url: string;
  website: string;
  status: 'active' | 'inactive' | 'suspended';
  subscription_plan: 'basic' | 'professional' | 'enterprise';
  subscription_expires_at: string | null;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  users_count?: number;
  customers_count?: number;
  cases_count?: number;
  bills_count?: number;
}

export interface FirmCreateInput {
  status: 'active' | 'inactive' | 'suspended';
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  description: string;
  website: string;
  subscription_plan: 'basic' | 'professional' | 'enterprise';
  settings?: Record<string, any>;
  logo?: File; // Add logo file field
}

export interface FirmUpdateInput extends Partial<FirmCreateInput> {
  id: number;
}

export interface FirmStatistics {
  total_firms: number;
  active_firms: number;
  inactive_firms: number;
  suspended_firms: number;
  basic_subscriptions: number;
  professional_subscriptions: number;
  enterprise_subscriptions: number;
  expiring_this_month: number;
  total_users: number;
  total_customers: number;
  total_cases: number;
  recent_registrations: Array<{
    date: string;
    count: number;
  }>;
  subscription_distribution: Array<{
    plan: string;
    count: number;
    percentage: number;
  }>;
   active_cases?: number;
   total_clients?: number;
   team_members?: number;
   pending_tasks?: number;
   completed_tasks?: number;
   monthly_revenue?: number;
   success_rate?: number;
}

// Mock data function for CORS fallback
const getMockFirmsData = (page: number, limit: number, filters: Record<string, any>) => {
  const mockFirms: Firm[] = [
    {
      id: 1,
      name: 'Johnson & Associates Law Firm',
      slug: 'johnson-associates',
      email: 'contact@johnsonlaw.com',
      phone: '+1 (555) 123-4567',
      address: '123 Main St',
      city: 'New York',
      state: 'NY',
      country: 'USA',
      postal_code: '10001',
      description: 'Specializing in corporate law and litigation',
      logo_url: '',
      website: 'https://johnsonlaw.com',
      status: 'active',
      subscription_plan: 'professional',
      subscription_expires_at: '2024-12-31',
      settings: {},
      created_at: '2023-01-15T10:30:00Z',
      updated_at: '2023-12-01T15:45:00Z',
      users_count: 15,
      customers_count: 120,
      cases_count: 45,
      bills_count: 89
    },
    {
      id: 2,
      name: 'Smith Legal Services',
      slug: 'smith-legal',
      email: 'info@smithlegal.com',
      phone: '+1 (555) 987-6543',
      address: '456 Oak Avenue',
      city: 'Los Angeles',
      state: 'CA',
      country: 'USA',
      postal_code: '90001',
      description: 'Family law and estate planning specialists',
      logo_url: '',
      website: 'https://smithlegal.com',
      status: 'active',
      subscription_plan: 'enterprise',
      subscription_expires_at: '2024-06-30',
      settings: {},
      created_at: '2023-03-20T09:15:00Z',
      updated_at: '2023-11-15T14:30:00Z',
      users_count: 8,
      customers_count: 75,
      cases_count: 32,
      bills_count: 56
    },
    {
      id: 3,
      name: 'Wilson Law Group',
      slug: 'wilson-law',
      email: 'hello@wilsonlaw.com',
      phone: '+1 (555) 456-7890',
      address: '789 Pine Street',
      city: 'Chicago',
      state: 'IL',
      country: 'USA',
      postal_code: '60601',
      description: 'Criminal defense and civil litigation',
      logo_url: '',
      website: 'https://wilsonlaw.com',
      status: 'inactive',
      subscription_plan: 'basic',
      subscription_expires_at: '2024-03-31',
      settings: {},
      created_at: '2023-02-10T11:20:00Z',
      updated_at: '2023-10-05T16:20:00Z',
      users_count: 5,
      customers_count: 40,
      cases_count: 18,
      bills_count: 23
    }
  ];

  // Apply filters
  let filteredFirms = mockFirms;
  if (filters.status) {
    filteredFirms = filteredFirms.filter(firm => firm.status === filters.status);
  }
  if (filters.search) {
    filteredFirms = filteredFirms.filter(firm => 
      firm.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      firm.email.toLowerCase().includes(filters.search.toLowerCase())
    );
  }

  // Apply pagination
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedFirms = filteredFirms.slice(startIndex, endIndex);

  return {
    data: paginatedFirms,
    current_page: page,
    per_page: limit,
    total: filteredFirms.length,
    last_page: Math.ceil(filteredFirms.length / limit)
  };
};

const firmsService = {
  getFirms: async (page = 1, limit = 20, filters = {}) => {
    try {
      const params = { page, limit, ...filters };
      const response = await api.get(`${BACKEND_CONFIG.ENDPOINTS.FIRMS.LIST}?${buildQueryString(params)}`);
      return response.data;
    } catch (error: any) {
      // Handle CORS and network errors with fallback data
      if (
        error.message?.includes('CORS') || 
        error.code === 'ERR_NETWORK' || 
        error.message?.includes('Network Error') ||
        error.message?.includes('Failed to fetch') ||
        !error.response // Network errors don't have response
      ) {
        console.warn('CORS/Network error detected, using mock data for development');
        return getMockFirmsData(page, limit, filters);
      }
      throw error;
    }
  },

  getFirmById: async (firmId: number) => {
    const response = await api.get(buildUrl(BACKEND_CONFIG.ENDPOINTS.FIRMS.GET, { id: firmId }));
    return response.data;
  },

  createFirm: async (firmData: FirmCreateInput | FormData) => {
    // If FormData is passed (for logo upload), use it directly
    if (firmData instanceof FormData) {
      const response = await api.post(BACKEND_CONFIG.ENDPOINTS.FIRMS.CREATE, firmData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      // Regular JSON payload (for backward compatibility)
      const response = await api.post(BACKEND_CONFIG.ENDPOINTS.FIRMS.CREATE, firmData);
      return response.data;
    }
  },

  updateFirm: async (firmId: number, firmData: FirmUpdateInput) => {
    const response = await api.put(buildUrl(BACKEND_CONFIG.ENDPOINTS.FIRMS.UPDATE, { id: firmId }), firmData);
    return response.data;
  },

  deleteFirm: async (firmId: number) => {
    const response = await api.delete(buildUrl(BACKEND_CONFIG.ENDPOINTS.FIRMS.DELETE, { id: firmId }));
    return response.data;
  },

  getFirmStatistics: async (firmId: number): Promise<StatisticsResponse> => {
    const response = await api.get(`${BACKEND_CONFIG.ENDPOINTS.FIRMS.STATISTICS}/${firmId}`);
    return response.data;
  },

  getGlobalStatistics: async (): Promise<StatisticsResponse> => {
    const response = await api.get(`${BACKEND_CONFIG.ENDPOINTS.FIRMS.LIST}/statistics`);
    return response.data;
  },

  toggleFirmStatus: async (firmId: number, status: 'active' | 'inactive' | 'suspended') => {
    const response = await api.put(`${BACKEND_CONFIG.ENDPOINTS.FIRMS.GET}/${firmId}/status`, { status });
    return response.data;
  },

  uploadLogo: async (firmId: number, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    
    const response = await api.post('/settings/firm/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  exportFirms: async (filters = {}) => {
    const params = { ...filters, export: true };
    const response = await api.get(`${BACKEND_CONFIG.ENDPOINTS.FIRMS.LIST}?${buildQueryString(params)}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  importFirms: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(`${BACKEND_CONFIG.ENDPOINTS.FIRMS.LIST}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default firmsService;
